import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

type Field = {
  id: string;
  type: 'short_text' | 'long_text' | 'single_select' | 'multi_select';
  label: string;
  placeholder: string | null;
  help_text: string | null;
  is_required: boolean;
  options: string[] | null;
};

type FormMeta = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  status: string;
  email_policy: 'off' | 'optional' | 'required_verified';
  closed: boolean;
};

export default function CustomForm() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormMeta | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/forms/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as { ok: boolean; form: FormMeta; fields: Field[] };
      })
      .then((data) => {
        if (cancelled) return;
        setForm(data.form);
        setFields(data.fields ?? []);
      })
      .catch((err) => {
        console.error('custom_form_fetch_failed', err);
        if (!cancelled) setError("We couldn't find that form.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function setResponse(id: string, value: unknown) {
    setResponses((prev) => ({ ...prev, [id]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const token = await getTurnstileToken();
      if (!token) throw new Error('turnstile_missing');
      const res = await fetch(`${API_BASE}/api/public/forms/${encodeURIComponent(slug)}/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, responses, turnstileToken: token }),
        signal: AbortSignal.timeout(15_000),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      navigate('/success');
    } catch (err) {
      console.error('custom_form_submit_failed', err);
      setError("That didn't send. Check the required fields and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <Shell><p className="text-burgundy-faint">Loading...</p></Shell>;
  }
  if (error && !form) {
    return <Shell><p className="text-danger font-bold">{error}</p></Shell>;
  }
  if (!form) return null;
  if (form.closed) {
    return (
      <Shell>
        <h1 className="font-bagel text-[42px] text-burgundy leading-none mb-3">Submissions closed.</h1>
        <p className="text-burgundy-soft">Follow @iswispod for what comes next.</p>
        <Link to="/" className="inline-block mt-6 text-pink-deep font-bold underline">Main dilemma form</Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="bg-cream-bright border-[3px] border-burgundy rounded-3xl shadow-chunk-8 p-5 sm:p-8">
        <h1 className="font-bagel text-[44px] sm:text-[64px] leading-[0.95] text-burgundy mb-3">
          {form.title}
        </h1>
        {form.description && <p className="text-burgundy-soft mb-6">{form.description}</p>}
        {error && <div className="mb-4 text-danger font-bold text-[13px]">{error}</div>}

        <div className="space-y-4">
          <FieldShell label="What should we call you?" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Anonymous, Big Dawg, Confused in Lekki..."
              className={inputClass}
            />
          </FieldShell>

          {form.email_policy !== 'off' && (
            <FieldShell label="Email" required={form.email_policy === 'required_verified'}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={form.email_policy === 'required_verified'}
                placeholder="you@somewhere.com"
                className={inputClass}
              />
            </FieldShell>
          )}

          {fields.map((field) => (
            <FieldShell key={field.id} label={field.label} required={field.is_required} help={field.help_text}>
              {renderField(field, responses[field.id], (value) => setResponse(field.id, value))}
            </FieldShell>
          ))}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-8 bg-pink text-cream-bright border-[3px] border-burgundy px-9 py-4 font-bagel text-[20px] tracking-[1px] rounded-full shadow-chunk-5 disabled:opacity-60"
        >
          {submitting ? 'SENDING...' : 'SEND IT IN'}
        </button>
      </form>
    </Shell>
  );
}

function renderField(field: Field, value: unknown, onChange: (value: unknown) => void) {
  if (field.type === 'long_text') {
    return (
      <textarea
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        required={field.is_required}
        placeholder={field.placeholder ?? ''}
        rows={5}
        className={inputClass}
      />
    );
  }
  if (field.type === 'single_select') {
    return (
      <select
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        required={field.is_required}
        className={inputClass}
      >
        <option value="">Choose one</option>
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'multi_select') {
    const selected = Array.isArray(value) ? value.map(String) : [];
    return (
      <div className="grid gap-2">
        {(field.options ?? []).map((option) => (
          <label key={option} className="flex items-center gap-2 text-burgundy-soft font-semibold">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) => {
                onChange(e.target.checked
                  ? [...selected, option]
                  : selected.filter((v) => v !== option));
              }}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }
  return (
    <input
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      required={field.is_required}
      placeholder={field.placeholder ?? ''}
      className={inputClass}
    />
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="paper-grain min-h-full bg-cream text-burgundy font-hanken px-4 py-8">
      <div className="max-w-[760px] mx-auto">
        <div className="font-rye text-center text-forest text-[24px] mb-8">I SAID WHAT I SAID</div>
        {children}
      </div>
    </div>
  );
}

function FieldShell({
  label,
  required,
  help,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-bold text-[14px] uppercase tracking-[0.5px] text-burgundy mb-2">
        {label} {required && <span className="text-pink">*</span>}
      </span>
      {children}
      {help && <span className="block mt-1 text-[12px] text-burgundy-faint">{help}</span>}
    </label>
  );
}

const inputClass =
  'w-full px-[16px] py-3 bg-cream border-2 border-burgundy rounded-[14px] font-hanken text-[16px] text-ink placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.2)]';

async function getTurnstileToken(): Promise<string> {
  const tt = (window as any).turnstile;
  if (!TURNSTILE_SITE_KEY || !tt) return '';
  return new Promise((resolve) => {
    const div = document.createElement('div');
    div.style.display = 'none';
    document.body.appendChild(div);
    try {
      tt.render(div, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'invisible',
        callback: (token: string) => {
          div.remove();
          resolve(token);
        },
        'error-callback': () => {
          div.remove();
          resolve('');
        },
      });
    } catch {
      div.remove();
      resolve('');
    }
    window.setTimeout(() => {
      div.remove();
      resolve('');
    }, 5000);
  });
}
